import React, { useEffect, useRef } from "react";
import { Spin, Divider } from "antd";
import {
  ProfileCardStyled,
  InsightsCollapseStyled,
  ProfileBodyStyled,
  ProfileLabelStyled,
} from "./EmployeeProfileCard.styles";

const CARD_BODY_STYLES = { body: { padding: 0 } };

const HEADER_STYLE = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E8EAF0",
  textAlign: "center",
  background: "#fff",
  borderRadius: "8px 8px 0 0",
  flexShrink: 0,
};

const SCROLL_CONTENT_STYLE = {
  overflowY: "auto",
  maxHeight: "clamp(180px, calc(100vh - 380px), 540px)",
  padding: "16px 20px",
};

// CandidateProfileV2 stacks this card below another one in a bounded-height
// column and wants it to genuinely scroll within whatever space is left,
// never pushing the column taller than the screen. A flat pixel height (280)
// doesn't work for that — it's either too tall for a short viewport (pushes
// the card past the fold) or leaves dead space on a tall one. `flex: 1` +
// `minHeight: 0` instead makes this box fill exactly whatever the column's
// flex layout leaves after the other card, in every viewport — see
// ALWAYS_SCROLLABLE_CARD_STYLES, which makes the antd Card and its body
// cooperate with that (a plain block Card would just grow to fit its
// content instead of being constrained by its flex parent).
const ALWAYS_SCROLLABLE_CONTENT_STYLE = {
  ...SCROLL_CONTENT_STYLE,
  overflowY: "scroll",
  flex: 1,
  minHeight: 0,
  maxHeight: "none",
};

const ALWAYS_SCROLLABLE_CARD_STYLES = {
  body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" },
};

const ALWAYS_SCROLLABLE_CARD_STYLE = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" };

const INSIGHT_LABELS = {
  skills_competencies:          "Skills & Competencies",
  career_aspirations:           "Career Aspirations",
  learning_development_needs:   "Learning & Development",
  engagement_motivation:        "Engagement & Motivation",
  performance_potential:        "Performance & Potential",
  internal_mobility:            "Internal Mobility",
  managerial_behavioral_skills: "Managerial / Behavioral Skills",
  retention_risk:               "Retention & Risk",
};

const INSIGHT_KEY_ORDER = Object.keys(INSIGHT_LABELS);

const EmployeeProfileCard = ({
  label,
  profileText,
  loading = false,
  isEmpty = false,
  insights = null,
  insightsLoading = false,
  keepScrollbarVisible = false,
}) => {
  const insightItems = INSIGHT_KEY_ORDER
    .filter((key) => insights?.[key])
    .map((key) => ({
      key,
      label: INSIGHT_LABELS[key],
      children: insights[key],
    }));

  const scrollBoxRef = useRef(null);
  const contentRef = useRef(null);

  // Collapsing an insight shrinks this content without the scroll box's own
  // height necessarily changing (it's bounded either way — by maxHeight in
  // the default case, by the flex parent in the keepScrollbarVisible case),
  // so nothing here naturally re-checks scrollTop against the new, shorter
  // scrollHeight — a scrolled-down view can end up pointing at space that no
  // longer exists, reading as "stuck" with no way back up. Watching the
  // actual content node (not the scroll box) catches every resize that
  // matters, expand or collapse, and re-clamps scrollTop into range whenever
  // it does.
  useEffect(() => {
    const box = scrollBoxRef.current;
    const content = contentRef.current;
    if (!box || !content) return undefined;
    const clampScroll = () => {
      const max = box.scrollHeight - box.clientHeight;
      if (box.scrollTop > max) box.scrollTop = Math.max(0, max);
    };
    const observer = new ResizeObserver(clampScroll);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <ProfileCardStyled
      styles={keepScrollbarVisible ? ALWAYS_SCROLLABLE_CARD_STYLES : CARD_BODY_STYLES}
      style={keepScrollbarVisible ? ALWAYS_SCROLLABLE_CARD_STYLE : undefined}
    >
      <div style={HEADER_STYLE}>
        <ProfileLabelStyled>{label}</ProfileLabelStyled>
      </div>

      <div style={keepScrollbarVisible ? ALWAYS_SCROLLABLE_CONTENT_STYLE : SCROLL_CONTENT_STYLE} ref={scrollBoxRef}>
        <div ref={contentRef}>
          <Spin spinning={loading}>
            {profileText && (
              <ProfileBodyStyled $centered={isEmpty}>
                {profileText}
              </ProfileBodyStyled>
            )}
          </Spin>

          <Spin spinning={insightsLoading}>
            {insightItems.length > 0 && (
              <>
                {profileText && <Divider style={{ margin: "8px 0" }} />}
                <InsightsCollapseStyled
                  items={insightItems}
                  defaultActiveKey={insightItems.map((item) => item.key)}
                  ghost
                />
              </>
            )}
          </Spin>
        </div>
      </div>
    </ProfileCardStyled>
  );
};

export default EmployeeProfileCard;
